import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

// RentalContractDto (Módulo 5) — um contrato de arrendamento.
// camelCase pinado a partir do contrato do backend; não depende do web-api-client.
export interface RentalContract {
  id: number;
  propertyId: number;
  propertyName: string;
  tenantId: number;
  tenantName: string;
  contractNumber: string | null;
  startDate: string;
  endDate: string | null;
  monthlyRent: number;
  paymentDueDay: number;
  deposit: number | null;
  statusId: number;
  statusCode: string;
  statusName: string;
  notes: string | null;
}

// Corpo de POST/PUT /api/rental-contracts.
export interface RentalContractPayload {
  propertyId: number;
  tenantId: number;
  contractNumber?: string | null;
  startDate: string;
  endDate?: string | null;
  monthlyRent: number;
  paymentDueDay: number;
  deposit?: number | null;
  statusId?: number | null;
  notes?: string | null;
}

// Tabela de referência de estados (GET .../statuses). Classificador = code.
export interface RentalContractStatus {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

// Filtros da lista — GET /api/rental-contracts.
export interface RentalContractFilters {
  propertyId?: number;
  tenantId?: number;
  statusCode?: string;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

export function getRentalContracts(
  params?: RentalContractFilters,
): Promise<RentalContract[]> {
  const search = new URLSearchParams();
  if (params?.propertyId != null) {
    search.set("propertyId", String(params.propertyId));
  }
  if (params?.tenantId != null) {
    search.set("tenantId", String(params.tenantId));
  }
  if (params?.statusCode && params.statusCode.trim() !== "") {
    search.set("statusCode", params.statusCode.trim());
  }
  const query = search.toString();
  return request<RentalContract[]>(
    `/api/rental-contracts${query ? `?${query}` : ""}`,
  );
}

export function getRentalContractById(id: number): Promise<RentalContract> {
  return request<RentalContract>(`/api/rental-contracts/${id}`);
}

export function getExpiringRentalContracts(
  withinDays = 60,
): Promise<RentalContract[]> {
  return request<RentalContract[]>(
    `/api/rental-contracts/expiring?withinDays=${withinDays}`,
  );
}

export function getRentalContractStatuses(): Promise<RentalContractStatus[]> {
  return request<RentalContractStatus[]>("/api/rental-contracts/statuses");
}

export function createRentalContract(
  payload: RentalContractPayload,
): Promise<RentalContract> {
  return request<RentalContract>("/api/rental-contracts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateRentalContract(
  id: number,
  payload: RentalContractPayload,
): Promise<RentalContract> {
  return request<RentalContract>(`/api/rental-contracts/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function deleteRentalContract(id: number): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/rental-contracts/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}
